import React from 'react';
import { useTranslation } from 'react-i18next';
import Dropdown from '../../../../components/Dropdown';
import { VALIDATION_FIELD_MAPPING } from '../../../../utils/appConstant';
import { getYearOptions } from '../../../../utils/methods';

interface StatementDetailsStepProps {
  formData: any;
  validation: any;
  onDropdownChange: (e: any, name: string) => void;
  onInputBlur: (e: any, name: string) => void;
  onFileChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onFileRemove: () => void;
  monthOptions?: any[];
  templateOptions?: any[];
  allTemplateOptions?: any[];
  stockistOptions?: any[];
  allStockistOptions?: any[];
  branchOptions?: any[];
  allBranchOptions?: any[];
  dropdownSearchData?: { month: string; year: string; stockist: string; branch: string; template: string };
  onDropdownSearchChange?: (e: any, name: string) => void;
}

const formatFileSize = (bytes: number) => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const StatementDetailsStep: React.FC<StatementDetailsStepProps> = ({
  formData,
  validation,
  onDropdownChange,
  onInputBlur,
  onFileChange,
  onFileRemove,
  monthOptions = [],
  templateOptions = [],
  allTemplateOptions = [],
  stockistOptions = [],
  allStockistOptions = [],
  branchOptions = [],
  allBranchOptions = [],
  dropdownSearchData,
  onDropdownSearchChange,
}) => {
  const { t } = useTranslation();

  const getErrorMessage = (field: string) => {
    if (validation.validate) return undefined;
    return validation.fields?.find((f: any) => f.field === field)?.message;
  };

  const fileError = getErrorMessage(VALIDATION_FIELD_MAPPING.file);

  return (
    <div className="card">
      <div className="card-body">
        <div className="row g-6">

          {/* Section 1 title */}
          <div className="col-12">
            <h6 className="mb-0">1. {t('monthlyStock.addEdit.pageTexts.form.sectionDetails')}</h6>
          </div>

          <div className="col-md-6">
            <div className="form-floating form-floating-outline">
              <Dropdown
                id={VALIDATION_FIELD_MAPPING.stockist}
                name={VALIDATION_FIELD_MAPPING.stockist}
                label={t('monthlyStock.addEdit.pageTexts.form.stockist')}
                placeholder={t('monthlyStock.addEdit.pageTexts.form.placeholders.stockist')}
                values={formData.stockist ? [formData.stockist] : []}
                options={stockistOptions}
                allOptions={allStockistOptions}
                searchText={dropdownSearchData?.stockist}
                onSearch={onDropdownSearchChange}
                onChange={(e) => onDropdownChange(e, VALIDATION_FIELD_MAPPING.stockist)}
                onBlur={(e) => onInputBlur(e, VALIDATION_FIELD_MAPPING.stockist)}
                error={!!getErrorMessage(VALIDATION_FIELD_MAPPING.stockist)}
                errorMessage={getErrorMessage(VALIDATION_FIELD_MAPPING.stockist)}
                required
              />
            </div>
          </div>

          <div className="col-md-6">
            <div className="form-floating form-floating-outline">
              <Dropdown
                id={VALIDATION_FIELD_MAPPING.branch}
                name={VALIDATION_FIELD_MAPPING.branch}
                label={t('monthlyStock.addEdit.pageTexts.form.branch')}
                placeholder={t('monthlyStock.addEdit.pageTexts.form.placeholders.branch')}
                values={formData.branch ? [formData.branch] : []}
                options={branchOptions}
                allOptions={allBranchOptions}
                searchText={dropdownSearchData?.branch}
                onSearch={onDropdownSearchChange}
                onChange={(e) => onDropdownChange(e, VALIDATION_FIELD_MAPPING.branch)}
                error={!!getErrorMessage(VALIDATION_FIELD_MAPPING.branch)}
                errorMessage={getErrorMessage(VALIDATION_FIELD_MAPPING.branch)}
                required
              />
            </div>
          </div>

          <div className="col-md-6">
            <div className="form-floating form-floating-outline">
              <Dropdown
                id={VALIDATION_FIELD_MAPPING.month}
                name={VALIDATION_FIELD_MAPPING.month}
                label={t('monthlyStock.addEdit.pageTexts.form.month')}
                placeholder={t('monthlyStock.addEdit.pageTexts.form.placeholders.month')}
                values={formData.month ? [formData.month] : []}
                options={monthOptions.filter(o => !dropdownSearchData?.month || o.label.toLowerCase().includes(dropdownSearchData.month.toLowerCase()))}
                allOptions={monthOptions}
                searchText={dropdownSearchData?.month}
                onSearch={onDropdownSearchChange}
                onChange={(e) => onDropdownChange(e, VALIDATION_FIELD_MAPPING.month)}
                onBlur={(e) => onInputBlur(e, VALIDATION_FIELD_MAPPING.month)}
                error={!!getErrorMessage(VALIDATION_FIELD_MAPPING.month)}
                errorMessage={getErrorMessage(VALIDATION_FIELD_MAPPING.month)}
                required
              />
            </div>
          </div>

          <div className="col-md-6">
            <div className="form-floating form-floating-outline">
              <Dropdown
                id="year"
                name="year"
                label={t('monthlyStock.addEdit.pageTexts.form.year')}
                placeholder={t('monthlyStock.addEdit.pageTexts.form.placeholders.year')}
                values={formData.year ? [formData.year] : []}
                options={getYearOptions().filter(o => !dropdownSearchData?.year || o.label.includes(dropdownSearchData.year))}
                allOptions={getYearOptions()}
                searchText={dropdownSearchData?.year}
                onSearch={onDropdownSearchChange}
                onChange={(e) => onDropdownChange(e, 'year')}
                onBlur={(e) => onInputBlur(e, 'year')}
                error={!!getErrorMessage('year')}
                errorMessage={getErrorMessage('year')}
                required
              />
            </div>
          </div>

          <div className="col-12">
            <div className="form-floating form-floating-outline">
              <Dropdown
                id={VALIDATION_FIELD_MAPPING.template}
                name={VALIDATION_FIELD_MAPPING.template}
                label={t('monthlyStock.addEdit.pageTexts.form.template')}
                placeholder={t('monthlyStock.addEdit.pageTexts.form.placeholders.template')}
                values={formData.template ? [formData.template] : []}
                options={templateOptions}
                allOptions={allTemplateOptions}
                searchText={dropdownSearchData?.template}
                onSearch={onDropdownSearchChange}
                onChange={(e) => onDropdownChange(e, VALIDATION_FIELD_MAPPING.template)}
                error={!!getErrorMessage(VALIDATION_FIELD_MAPPING.template)}
                errorMessage={getErrorMessage(VALIDATION_FIELD_MAPPING.template)}
              />
            </div>
          </div>

          {/* Section 2 title */}
          <div className="col-12">
            <h6 className="mb-0">2. {t('monthlyStock.addEdit.pageTexts.form.sectionFile')}</h6>
          </div>

          <div className="col-12">
            {!formData.file ? (
              <div className="position-relative">
                <input
                  type="file"
                  className="position-absolute w-100 h-100 opacity-0 cursor-pointer"
                  style={{ zIndex: 1 }}
                  accept=".xlsx,.xls,.txt"
                  onChange={onFileChange}
                />
                <div
                  className={`border border-dashed rounded-2 text-center bg-label-secondary${fileError ? ' border-danger' : ''}`}
                  style={{ minHeight: '180px', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '2rem' }}
                >
                  <i className="ri-cloud-upload-line text-primary mb-3" style={{ fontSize: '3rem' }}></i>
                  <h5 className="fw-semibold mb-1">{t('monthlyStock.addEdit.pageTexts.form.dropzoneText')}</h5>
                  <p className="text-muted small mb-3">{t('monthlyStock.addEdit.pageTexts.form.dropzoneSubtext')}</p>
                  <div className="d-flex gap-2 justify-content-center flex-wrap">
                    {['.xlsx', '.xls', '.txt'].map((ext) => (
                      <span key={ext} className="badge bg-label-primary fw-normal">{ext}</span>
                    ))}
                  </div>
                </div>
              </div>
            ) : (
              <div className={`d-flex align-items-center p-4 rounded-2 bg-label-success border border-success-subtle${fileError ? ' border-danger bg-label-danger' : ''}`}>
                <div className="me-3">
                  <i className="ri-file-excel-2-line text-success" style={{ fontSize: '2rem' }}></i>
                </div>
                <div className="flex-grow-1">
                  <p className="fw-semibold mb-0 text-body">{formData.file.name}</p>
                  <small className="text-muted">{formatFileSize(formData.file.size)}</small>
                </div>
                <button
                  type="button"
                  className="btn btn-sm btn-icon btn-text-danger rounded-pill waves-effect"
                  onClick={onFileRemove}
                  title={t('monthlyStock.addEdit.pageTexts.form.removeFile')}
                >
                  <i className="ri-close-line ri-20px"></i>
                </button>
              </div>
            )}
            {fileError && (
              <div className="invalid-feedback d-block mt-1">{fileError}</div>
            )}
          </div>

        </div>
      </div>
    </div>
  );
};

export default StatementDetailsStep;
